class LocalizationSelector extends HTMLElement {
  #input;
  #form;
  #searchableOptions;
  #languageSelect;
  #languageControl;
  #boundHandleInput;
  #boundHandleChange;
  #boundHandleSubmit;
  #searchTimer = null;
  #pendingSearchTerm = "";
  #hiddenCheckedStates = new Map();

  constructor() {
    super();

    const input = this.querySelector('[data-part="input"]');
    const form = this.querySelector('[data-part="form"]');
    const options = this.querySelectorAll('[data-part="option"]');

    if (!input || !form || options.length === 0) {
      throw new Error("LocalizationSelector: Required elements are missing");
    }

    this.#input = input;
    this.#form = form;
    this.#searchableOptions = Array.from(options, (option) => {
      const label = option.querySelector("label");
      const countryInput = option.querySelector('input[name="country_code"]');
      const labelText = this.#normalizeText(
        label?.textContent ?? option.textContent ?? "",
      );
      const value = this.#normalizeText(countryInput?.value ?? "");

      return {
        countryInput,
        labelText,
        option,
        value,
      };
    });
    this.#languageSelect = null;
    this.#languageControl = this.closest("x-drawer")?.querySelector(
      '[data-part="language-control"]',
    );
    this.#boundHandleInput = this.#handleInput.bind(this);
    this.#boundHandleChange = this.#handleChange.bind(this);
    this.#boundHandleSubmit = this.#handleSubmit.bind(this);
  }

  connectedCallback() {
    this.#input.addEventListener("input", this.#boundHandleInput);
    this.#form.addEventListener("change", this.#boundHandleChange);
    this.#form.addEventListener("submit", this.#boundHandleSubmit);

    setTimeout(() => this.#syncLanguageSelect(), 0);
  }

  disconnectedCallback() {
    this.#input.removeEventListener("input", this.#boundHandleInput);
    this.#form.removeEventListener("change", this.#boundHandleChange);
    this.#form.removeEventListener("submit", this.#boundHandleSubmit);

    if (this.#searchTimer !== null) {
      clearTimeout(this.#searchTimer);
      this.#searchTimer = null;
    }
  }

  #syncLanguageSelect() {
    this.#languageSelect = this.closest("x-drawer")?.querySelector(
      '[data-part="language"]',
    );

    const selectedCountry = this.#form.querySelector(
      'input[name="country_code"]:checked',
    );

    if (selectedCountry) {
      this.#renderLanguages(this.#getLanguages(selectedCountry));
    }
  }

  #handleInput(event) {
    this.#pendingSearchTerm = event.target.value;

    if (this.#searchTimer !== null) {
      clearTimeout(this.#searchTimer);
    }

    this.#searchTimer = setTimeout(() => {
      this.#searchTimer = null;
      this.#filterOptions(this.#normalizeText(this.#pendingSearchTerm));
    }, 250);
  }

  #filterOptions(searchTerm) {
    for (const { countryInput, labelText, option, value } of this
      .#searchableOptions) {
      const matches =
        searchTerm === "" ||
        labelText.includes(searchTerm) ||
        value.includes(searchTerm);

      option.hidden = !matches;

      if (!matches) {
        if (countryInput?.checked) {
          this.#hiddenCheckedStates.set(countryInput, true);
          countryInput.checked = false;
        }
        continue;
      }

      if (countryInput && this.#hiddenCheckedStates.get(countryInput)) {
        countryInput.checked = true;
        this.#hiddenCheckedStates.delete(countryInput);
      }
    }
  }

  #normalizeText(text) {
    return text
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }

  #handleChange(event) {
    const countryInput = event.target;

    if (
      !(countryInput instanceof HTMLInputElement) ||
      countryInput.name !== "country_code"
    ) {
      return;
    }

    this.#hiddenCheckedStates.clear();
    this.#renderLanguages(this.#getLanguages(countryInput));
  }

  #getLanguages(countryInput) {
    try {
      return JSON.parse(countryInput.dataset.languages || "[]");
    } catch {
      return [];
    }
  }

  #renderLanguages(languages) {
    if (!this.#languageSelect) {
      this.#languageSelect = this.closest("x-drawer")?.querySelector(
        '[data-part="language"]',
      );
    }

    if (!this.#languageSelect) {
      return;
    }

    const previousSelected = this.#languageSelect.value;
    const availableLanguages = languages;

    this.#languageSelect.replaceChildren();

    if (this.#languageControl) {
      this.#languageControl.hidden = availableLanguages.length <= 1;
    }

    availableLanguages.forEach((language) => {
      const option = document.createElement("option");
      option.value = language.iso_code;
      option.textContent = language.name;
      option.selected = language.iso_code === previousSelected;

      this.#languageSelect.appendChild(option);
    });

    if (
      !availableLanguages.some(
        (language) => language.iso_code === previousSelected,
      ) &&
      availableLanguages[0]
    ) {
      this.#languageSelect.value = availableLanguages[0].iso_code;
    }
  }

  #handleSubmit() {
    const selectedCountry = this.#form.querySelector(
      'input[name="country_code"]:checked',
    );
    const selectedCountryCode = selectedCountry?.value?.toUpperCase();

    if (!selectedCountryCode) {
      return;
    }

    const formAction = new URL(this.#form.action, window.location.origin);
    formAction.searchParams.set("country", selectedCountryCode);
    this.#form.action = formAction.toString();
  }
}

if (!window.customElements.get("localization-selector")) {
  window.customElements.define("localization-selector", LocalizationSelector);
}
